/**
 * Auth utilisateur (cabinet) — distinct de admin-api.
 *
 * Token stocké dans localStorage (`mizan_user_token`). Auto-purge sur 401.
 * Attaché aux requêtes /api/* via le client api standard (lib/api.ts).
 */

const TOKEN_KEY = 'mizan_user_token';
const EXPIRES_KEY = 'mizan_user_token_exp';
const USER_KEY = 'mizan_user';

const DEFAULT_API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

export interface AuthenticatedUser {
  id: string;
  email: string;
  full_name: string;
  firm_id: string;
  role: 'owner' | 'accountant' | 'assistant' | 'viewer';
  allowed_company_ids?: string[];
}

export async function login(email: string, password: string): Promise<{ token: string; user: AuthenticatedUser; expires_at: string }> {
  const res = await fetch(`${DEFAULT_API_BASE}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const text = await res.text();
  let body: unknown = text;
  try {
    body = JSON.parse(text);
  } catch {
    /* */
  }
  if (!res.ok) {
    const message =
      typeof body === 'object' && body !== null && 'message' in body
        ? String((body as { message: unknown }).message)
        : `HTTP ${res.status}`;
    throw new Error(message);
  }
  const data = body as { token: string; expires_at: string; user: AuthenticatedUser };
  saveUserSession(data);
  return data;
}

export async function fetchMe(): Promise<AuthenticatedUser> {
  const token = getUserToken();
  if (!token) throw new Error('not_authenticated');
  const res = await fetch(`${DEFAULT_API_BASE}/api/auth/me`, {
    headers: { Authorization: `Bearer ${token.token}` },
  });
  if (!res.ok) {
    if (res.status === 401) clearUserSession();
    throw new Error(`HTTP ${res.status}`);
  }
  const data = (await res.json()) as { user: AuthenticatedUser };
  return data.user;
}

export function saveUserSession(data: { token: string; expires_at: string; user: AuthenticatedUser }): void {
  localStorage.setItem(TOKEN_KEY, data.token);
  localStorage.setItem(EXPIRES_KEY, data.expires_at);
  localStorage.setItem(USER_KEY, JSON.stringify(data.user));
}

export function getUserToken(): { token: string; expires_at: string } | null {
  const token = localStorage.getItem(TOKEN_KEY);
  const exp = localStorage.getItem(EXPIRES_KEY);
  if (!token || !exp) return null;
  if (new Date(exp).getTime() < Date.now()) {
    clearUserSession();
    return null;
  }
  return { token, expires_at: exp };
}

export function getUserSession(): AuthenticatedUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthenticatedUser;
  } catch {
    return null;
  }
}

export function clearUserSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(EXPIRES_KEY);
  localStorage.removeItem(USER_KEY);
}
